import styled from "@emotion/styled";
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";

import { numberFormatLong } from "components/app_layout/Typography/Numbers";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
} from "components/ui/chart/Chart";
import { useLegendToggle } from "components/ui/chart/useLegendToggle";
import EmptyState from "components/ui/empty_state/EmptyState";
import { formatShortAmount } from "lib/utils";
import { ChartSkeleton } from "./ChartSkeleton";
import { ClickToInspectTooltipContent } from "./ClickToInspectTooltipContent";
import { formatSpendValue, TrendRow } from "./spendChartRows";

type Props = {
  rows: TrendRow[];
  isLoading?: boolean;
  /** Called with the ISO start of the clicked period. */
  onPeriodClick: (periodStart: string) => void;
};

const CHART_HEIGHT = 470;

const SERIES_KEYS = ["total", "transactions"];

// Spend is the subject and the count is context, so they are not two peers competing for
// attention: the brand colour carries the bars and the count rides over them as ink.
// Colours are set explicitly rather than left to fall through to palette slots, which are
// handed out by config key order and would move if anyone reordered this object.
const CONFIG: ChartConfig = {
  total: {
    // The legend renders nothing without a label. Unlike the tooltip, it has no
    // fallback to the series name.
    label: "Total spend",
    color: "sysChart1",
    tooltipFormatter: (value: number) => formatSpendValue(value),
  },
  transactions: {
    label: "Transactions",
    // A text token, so the line reads as ink against the bars and inverts to a light
    // stroke in dark mode.
    color: "sysTextDefault",
    tooltipFormatter: (value: number) => numberFormatLong(value),
  },
};

/**
 * Spend and transaction count per period, on two y axes. Clicking a period asks the
 * caller to show the transactions behind it.
 */
export default function SpendTrendChart({ rows, isLoading, onPeriodClick }: Props) {
  const { legendProps, seriesProps } = useLegendToggle(SERIES_KEYS);

  if (isLoading) {
    return (
      <ChartFrame>
        <ChartSkeleton height={CHART_HEIGHT} label="Loading spend chart" />
      </ChartFrame>
    );
  }

  if (rows.length === 0) {
    return (
      <ChartFrame>
        <EmptyState
          type="noResults"
          heading="No spend in this period"
          description="Try widening the date range, or removing a filter."
        />
      </ChartFrame>
    );
  }

  return (
    <StyledChartContainer config={CONFIG}>
      <ComposedChart
        accessibilityLayer
        data={rows}
        margin={{ left: 12, right: 12 }}
        // A whole period is the click target, not just its bar, which on a quiet period
        // is a few pixels tall. recharts only calls this for a click inside the plot
        // area, so the legend and the axes below it can't reach it.
        onClick={(state) => {
          const row = rows[state?.activeTooltipIndex ?? -1];
          if (row) {
            onPeriodClick(row.periodStart);
          }
        }}
      >
        <CartesianGrid vertical={false} />
        <XAxis dataKey="period" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} />
        <YAxis
          yAxisId="total"
          width={72}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value: number) => formatShortAmount(value)}
          // "auto" rather than the data max: pinning the top to the exact maximum makes
          // the last gridline land on a raw value, so the axis reads
          // 0 / 55B / 110B / 214.7B instead of even steps.
          domain={[0, "auto"]}
          label={{ value: "Value GBP", angle: -90, position: "insideLeft" }}
          // Hidden with its series, so isolating one from the legend doesn't leave an
          // axis standing with nothing plotted against it.
          {...seriesProps("total")}
        />
        <YAxis
          yAxisId="transactions"
          orientation="right"
          width={72}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
          domain={[0, "auto"]}
          label={{ value: "Transactions", angle: 90, position: "insideRight" }}
          {...seriesProps("transactions")}
        />
        <ChartTooltip
          // Spend first whichever order the series are drawn in. Ranking by value would
          // be meaningless here: one row is pounds and the other is a count.
          itemSorter={(item) => (item.dataKey === "total" ? 0 : 1)}
          content={<ClickToInspectTooltipContent />}
        />
        <ChartLegend verticalAlign="bottom" content={<ChartLegendContent {...legendProps} />} />
        {/* Spend gets the bars: length is the best mark for comparing one period against
            another, and the money is what the page is about. */}
        <Bar
          yAxisId="total"
          dataKey="total"
          fill="var(--color-total)"
          radius={4}
          isAnimationActive={false}
          {...seriesProps("total")}
        />
        {/* Declared after the bars so the line draws on top of them rather than behind. */}
        <Line
          yAxisId="transactions"
          dataKey="transactions"
          type="linear"
          stroke="var(--color-transactions)"
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
          activeDot={{ r: 5 }}
          {...seriesProps("transactions")}
        />
      </ComposedChart>
    </StyledChartContainer>
  );
}

const ChartFrame = styled.div({
  height: CHART_HEIGHT,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledChartContainer = styled(ChartContainer)({
  height: CHART_HEIGHT,
  width: "100%",
  // Every period is clickable, so the whole plot takes the pointer. The legend is an
  // HTML sibling of the svg and keeps its own cursor.
  "& .recharts-surface": {
    cursor: "pointer",
  },
});
